import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../App";
import { useTranslation } from "react-i18next";
import { ConvertMmToPx, formatDate } from "../../../hooks/Method";
import Barcode from "react-barcode/lib/react-barcode";

const ThermalPrinterRecept = ({ data }) => {
  const { settings, selStore, stores } = useContext(AppContext);
  const [store, setStore] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    setStore({ ...stores.find((j) => j.id === selStore) });
    return () => {};
  }, [selStore, stores]);

  return data.id && store.id ? (
    <div
      style={{
        maxWidth: `${
          store.receiptWidth ? ConvertMmToPx(store.receiptWidth) : "220"
        }px`,
        width: "100%",
        fontFamily: "monospace",
        fontSize: 10,
        fontWeight: 600,
        margin: 0,
        padding: 0,
        color: "#000000",
        lineHeight: "1.2",
        whiteSpace: "pre-wrap",
      }}
    >
      {/* Store Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "4px",
          paddingBottom: "4px",
          borderBottom: "2px dashed #333",
        }}
      >
        {store.logo && store.showLogoOnReceipt === true && (
          <div
            style={{
              marginBottom: "5px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={store.logo}
              alt="store_logo"
              style={{
                maxHeight: "100px",
                maxWidth: "150px",
                objectFit: "contain",
              }}
            />
          </div>
        )}
        {store.showStoreNameOnReceipt && (
          <>
            <div style={{ fontSize: "14px" }}>{store.storeName}</div>
            {store.name.localName && (
              <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                {store.localName}
              </div>
            )}
          </>
        )}
        {store.showTaglineOnReceipt && (
          <div
            style={{
              display: "grid",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontStyle: "italic",
                letterSpacing: "0.5px",
                padding: "0px 5px",
                border: "2px dashed #000000",
                textAlign: "center",
                width: "fit-content",
                marginBottom: "5px",
              }}
            >
              <span>{store.tagline}</span>
            </div>
          </div>
        )}
        {(store.showAddressOnReceipt || store.showPhoneOnReceipt) && (
          <div>
            {store.showAddressOnReceipt && (
              <>
                {store.address && (
                  <span
                    style={{
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {store.address}
                  </span>
                )}
                <div>
                  {store.city ? `, ${store.city}` : ""}
                  {store.state ? `, ${store.state}` : ""}
                  {store.zip ? `, ${store.zip}` : ""}
                </div>
              </>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "5px",
              }}
            >
              {store.showPhoneOnReceipt && store.phone && (
                <span>{store.phone}</span>
              )}
              {store.showEmailOnReceipt && store.email && (
                <span>{store.email}</span>
              )}
              {store.website && <span>{store.website}</span>}
              {store.socialMedia && (
                <span style={{ whiteSpace: "pre-wrap" }}>
                  {store.socialMedia}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details */}
      <div
        style={{
          display: "grid",
          gap: "2px",
          marginBottom: "10px",
        }}
      >
        <div style={{ display: "flex", gap: "5px" }}>
          <span>{t("invoiceNo")}:</span>
          <span>#{data.sellId}</span>
        </div>

        <div style={{ display: "flex", gap: "5px" }}>
          <span>{t("sellReturnId")}</span>
          <span>#{data.id}</span>
        </div>

        <div style={{ display: "flex", gap: "5px", fontSize: 9 }}>
          <span>{t("sellDate")}:</span>
          <span>{formatDate(data.sell.sellDate)}</span>
        </div>

        <div style={{ display: "flex", gap: "5px", fontSize: 9 }}>
          <div>{t("returnDate")}:</div>
          <div>{formatDate(data.refundDate)}</div>
        </div>

        <div style={{ display: "flex", gap: "5px", fontSize: 9 }}>
          <span>{t("customer")}:</span>
          <span>
            {data.sell.cstmr ? data.sell.cstmr.fullName : t("walkInCustomer")}
          </span>
        </div>

        {data.sell.cstmr && (
          <>
            <div style={{ display: "flex", gap: "5px" }}>
              <span>{t("phone")}:</span>
              <span>{data.sell.cstmr.phone}</span>
            </div>
          </>
        )}
      </div>

      {/* Items Table */}
      <div
        style={{
          marginBottom: "5px",
          borderBottom: "2px dashed #000000",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "4fr 1fr 2fr",
            gap: "2px",
            borderTop: "1px dashed #000000",
            borderBottom: "1px dashed #000000",
            marginBottom: "5px",
          }}
        >
          <div>{t("itemName")}</div>
          <div style={{ textAlign: "center" }}>{t("qty")}</div>
          <div style={{ textAlign: "right" }}>{t("price")}</div>
        </div>

        {data.items?.map((item, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "4fr 1fr 2fr",
              gap: "1px",
              marginBottom: "6px",
            }}
          >
            <div>{item.product.label}</div>
            <div style={{ textAlign: "center" }}>{item.returnQty}</div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol}{" "}
              {(item.returnQty * item.price).toFixed(settings.decimal)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals Section */}
      <div
        style={{
          display: "grid",
          gap: "6px",
          marginBottom: "5px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{t("refund")}:</span>
          <span>
            {settings.curSymbol}{" "}
            {(data.refundAmt || 0).toFixed(settings.decimal)}
          </span>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{t("discount")} </span>
          <span>
            (-) {settings.curSymbol}{" "}
            {(data.cutDiscount || 0).toFixed(settings.decimal)}
          </span>
        </div>

        {data.refundTaxAmt > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("tax")}:</span>
            <span>
              (+) {settings.curSymbol}{" "}
              {(data.refundTaxAmt || 0).toFixed(settings.decimal)}
            </span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "600",
            paddingTop: "2px",
            borderTop: "1px dashed #000000",
          }}
        >
          <span>
            {data.keepRefundAsCredit ? t("credited") : t("payableRefund")}:
          </span>
          <span>
            {settings.curSymbol} {data.payableRefund.toFixed(settings.decimal)}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: "grid",
          justifyContent: "center",
          alignItems: "center",
          textTransform: "uppercase",
          fontSize: "18px",
          borderBottom: "2px dashed #000000",
          borderTop: "2px dashed #000000",
          padding: "8px 0px",
          marginTop: "5px",
          letterSpacing: "2px",
        }}
      >
        ✦ {t("thankYou")} ✦
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        {data.sellId && (
          <Barcode
            value={data.sellId.toString()}
            displayValue={true}
            height={30}
            width={2}
            fontSize={14}
          />
        )}
      </div>
    </div>
  ) : null;
};

export default ThermalPrinterRecept;
